import { Button, Input, Label, Textarea } from "@ondit/ui";
/**
 * How this brand asks to be written about.
 *
 * The brief already decides what a page may *say*; nothing decided how it
 * *reads*, so every page came out in one voice using whatever word the model
 * reached for. This is the other half of the same shelf — filing, set once and
 * revisited when a page reads wrong.
 *
 * Terms are pairs and never a bare ban, and the screen says so: deleting a word
 * out of the middle of a sentence leaves prose nobody wrote. Every page is
 * already held to the house list below, which is shown read-only so a brand can
 * see what it does not have to restate.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { useAccess } from "~/core/access/index";
import { ApiError, api, type VoiceTerm } from "~/core/api/index";
import { useI18n, useT } from "~/core/i18n";
import { keys, read } from "~/core/query";
import { Section } from "~/core/ui/page";
import { Async } from "~/core/ui/states";

export function BrandVoice({ entityId }: { entityId: string }) {
	const t = useT();
	const { editor } = useAccess();
	const { locale } = useI18n();
	const queryClient = useQueryClient();
	const state = useQuery({
		queryKey: [...keys.entity(entityId), "voice"],
		queryFn: () => api.brandVoice(entityId),
		...read,
	});
	const store = useMutation({
		mutationFn: (body: Parameters<typeof api.saveBrandVoice>[1]) => api.saveBrandVoice(entityId, body),
		onSuccess: () => void queryClient.invalidateQueries({ queryKey: keys.entity(entityId) }),
	});
	const [voice, setVoice] = useState<string | null>(null);
	const [terms, setTerms] = useState<VoiceTerm[] | null>(null);
	const [busy, setBusy] = useState(false);

	async function save(current: { voice: string; terms: VoiceTerm[] }) {
		setBusy(true);
		try {
			await store.mutateAsync({
				voice: voice ?? current.voice,
				terms: (terms ?? current.terms).filter((term) => term.use_term.trim() && term.instead_of.trim()),
			});
			toast.success(t("common.saved"));
			setVoice(null);
			setTerms(null);
		} catch (e) {
			toast.error(e instanceof ApiError ? e.localized(locale) : String(e));
		} finally {
			setBusy(false);
		}
	}

	return (
		<Async state={state}>
			{(data) => {
				const rows = terms ?? data.terms;
				const dirty = (voice ?? data.voice) !== data.voice || JSON.stringify(rows) !== JSON.stringify(data.terms);
				const incomplete = rows.some((term) => !term.use_term.trim() || !term.instead_of.trim());
				const edit = (index: number, patch: Partial<VoiceTerm>) =>
					setTerms(rows.map((term, i) => (i === index ? { ...term, ...patch } : term)));
				return (
					<fieldset className="bare-fieldset brand-voice-form" disabled={!editor || busy}>
						<Section title={t("voice.title")} lede={t("voice.hint")}>
							<div className="field wide">
								<Textarea
									id="brand-voice"
									aria-label={t("voice.title")}
									maxLength={300}
									onChange={(e) => setVoice(e.target.value)}
									placeholder={t("voice.tonePlaceholder")}
									rows={2}
									value={voice ?? data.voice}
								/>
								<span className="hint">{t("voice.toneHint")}</span>
							</div>
						</Section>

						<Section title={t("voice.terms")} lede={t("voice.termsHint")}>
							<div className="voice-terms">
								{rows.length === 0 ? (
									<p className="hint">{t("voice.empty")}</p>
								) : (
									<div className="voice-term-list">
										{rows.map((term, index) => (
											// biome-ignore lint/suspicious/noArrayIndexKey: editable values cannot serve as identity
											<div className="voice-term" key={index}>
												<div className="voice-term-field">
													<Label htmlFor={`voice-use-${index}`}>{t("voice.write")}</Label>
													<Input
														id={`voice-use-${index}`}
														maxLength={60}
														value={term.use_term}
														onChange={(e) => edit(index, { use_term: e.target.value })}
													/>
												</div>
												<div className="voice-term-field">
													<Label htmlFor={`voice-avoid-${index}`}>{t("voice.never")}</Label>
													<Input
														id={`voice-avoid-${index}`}
														maxLength={60}
														value={term.instead_of}
														onChange={(e) => edit(index, { instead_of: e.target.value })}
													/>
												</div>
												<Button
													type="button"
													variant="ghost"
													size="icon"
													aria-label={t("common.remove")}
													onClick={() => setTerms(rows.filter((_, i) => i !== index))}
												>
													<Trash2 aria-hidden />
												</Button>
											</div>
										))}
									</div>
								)}
								<div className="row-actions">
									<Button
										type="button"
										variant="default"
										disabled={rows.length >= 20}
										onClick={() => setTerms([...rows, { use_term: "", instead_of: "" }])}
									>
										<Plus aria-hidden />
										{t("voice.add")}
									</Button>
									<Button type="button" disabled={!dirty || incomplete} onClick={() => save(data)}>
										{t("common.save")}
									</Button>
									{incomplete ? <span className="hint">{t("voice.completePair")}</span> : null}
								</div>
							</div>
						</Section>

						<Section title={t("voice.house")} lede={t("voice.houseHint")}>
							<ul className="chips">
								{data.house.map((term) => (
									<li className="pill" key={term.instead_of}>
										<s>{term.instead_of}</s> → {term.use_term}
									</li>
								))}
							</ul>
						</Section>
					</fieldset>
				);
			}}
		</Async>
	);
}
